import {Button, Box, Chip, Divider, Stack, Typography, useTheme, ToggleButton, ToggleButtonGroup,} from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {useEffect, useState} from "react";
import {ColDef, ValueFormatterParams } from "ag-grid-community";
import {ApiRequestFailed, authenticateAndFetchData, retryFn} from "@lib/apis";
import {useQuery} from "@tanstack/react-query";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {Link, useParams} from "react-router-dom";
import {CustomCellRendererProps} from "ag-grid-react";
import {urls} from "@routes";
import { format } from "date-fns";
import {UserDetailsLink} from "./AdminUsers"
import { useSnackbar } from "notistack";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface Campaigns {
  campaign_id: string | number;
  campaign_name: string;
  user_id: number;
  user_email: string;
  archived: boolean;
  status: string;
  created_on_ts: number;
  campaign_type: string
  campaign_type_display: string
  total_reply: number
  reply_rate: number
}

export interface AdminCampaignExportItem {
    uid: string;
    name: string;
    user_email: string;
    workspace_id: string;
    status: string;
    created_on: string;
    sending_domains: string;
    contacts_count: number;
    emails_sent: number;
    leads_contacted: number;
    replies_count: number;
    total_open_count: number;
    unique_open_count: number;
    forced_open_count: number;
    sequence_count: number;
    has_plain_text_email: boolean;
    open_tracking_enabled: boolean;
    click_tracking_enabled: boolean;
    is_campaign_bounce_blocked: boolean;
    daily_email_limit: number;
    archived: boolean;
    started_running_at: string;
    completed_at: string;
    cancelled_at: string;
}

interface PaginationData {
	data: Campaigns[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
}


type StatusFilter = "all" | "ongoing" | "completed" | "cancelled" | "archived";

export default function AdminAllCampaign() {
    const {userId} = useParams();
    const { user } = useAuth();
    const theme = useTheme();
    const {enqueueSnackbar} = useSnackbar();    
    
    const [pageData, setPageData] = useState<PaginationData>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("ongoing");
    const [exportLoading, setExportLoading] = useState(false);        
    // const [filteredCampaigns, setFilteredCampaigns] = useState<PageData[]>([]);

    // Query to fetch all campaigns.
    const adminAllCampaignsQuery = useQuery({
        queryKey: [
            "adminAllCampaignsQuery",
            page,
            pageSize,
            search,
            statusFilter
        ],
        queryFn: () =>
            authenticateAndFetchData(
                `/admin/admin-get-all-campaign/?page=${page}&page_size=${pageSize}&search=${search}&status=${statusFilter}`
            ),
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: retryFn,
    });
    
    useEffect(() => {
        if (adminAllCampaignsQuery.data?.data) {
            const response = adminAllCampaignsQuery.data.data;

            setPageData({
                data: response.data || [],
                total_count: response.total_count,
                total_pages: response.total_pages,
                current_page: response.current_page,
                page_size: response.page_size,
            });
        }
    }, [adminAllCampaignsQuery.data]);

    const fetchAllCampaignExportQuery = useQuery({
        queryKey: ["adminAllCampaignExportQuery"],
        queryFn: () => {
            let url = `/admin/admin-export-all-campaigns/`;
            return authenticateAndFetchData(url);
        },
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: retryFn,
    });
    

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    function handleStatusFilterChange(
        _event: React.MouseEvent<HTMLElement>,
        newValue: string | null
    ) {
        if (newValue) {
            setStatusFilter(newValue as StatusFilter);
            setPage(1);
        }
    }

    const CampaignDetailsLink = (cellProps: CustomCellRendererProps) => {
            return (
                <Link
                    to={urls["adminUserCampaignDetails"].replace(":userId", cellProps.data["user_id"] || "").replace(":campaignUID", cellProps.data["campaign_id"])}
                    state={{ from: "AllCampaign" }}
                    style={{color: theme.palette.text.primary}}>
                    {cellProps.value}
                </Link>
            )
        }
    
        function campaignStatusChipColor(status: string) {
                switch (status) {
                    case "creating":
                        return "warning";
        
                    case "created":
                        return "default";
        
                    case "scheduled":
                        return "primary";
        
                    case "running":
                        return "primary";
        
                    case "complete":
                        return "success";
        
                    case "cancelled":
                        return "error";
        
                    default:
                        return "default";
                }
            }
        
        const StatusBadge = (params: CustomCellRendererProps) => {
            return (
                <Chip size={"small"} color={campaignStatusChipColor(params.value)} label={params.value}/>
            )
        }
    
    function timestampFormatter(params: ValueFormatterParams) {
            if (params.value === null) {
                return "---";
            }
            return format(new Date(params.value), "do MMM y, h:mm a");
        }

    const TableTopRow = (props: {
        value: StatusFilter,
        onChange: (event: React.MouseEvent<HTMLElement>, newValue: string | null) => void,
    }) => {
        return (
            <Box 
                sx={{
                    width: "100%", 
                    overflowX: "auto",
                    py: 0.5,                    
                    WebkitOverflowScrolling: "touch",
                    '&::-webkit-scrollbar': { display: 'none' }
                }}
            >
                <ToggleButtonGroup
                    color="primary"
                    value={props.value}
                    exclusive
                    onChange={props.onChange}
                    size="small"
                    sx={{
                        whiteSpace: "nowrap",
                        display: "inline-flex"
                    }}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="ongoing">Ongoing</ToggleButton>
                    <ToggleButton value="completed">Completed</ToggleButton>
                    <ToggleButton value="cancelled">Cancelled</ToggleButton>
                    <ToggleButton value="archived">Archived</ToggleButton>
                </ToggleButtonGroup>
            </Box>
        )
    }
    
    const columnCustomizerLabel = {
		campaign_id: "Campaign ID",
		campaign_name: "Campaign Name",
		user_email: "User Email",		
		status: "Campaign Status",
        created_on_ts: "Created On"				
	};

    const columnDefs: ColDef[] = [
            {field: "campaign_id", headerName: "Campaign ID", cellRenderer: CampaignDetailsLink, flex: 2},
            {field: "campaign_name", headerName: "Campaign Name", cellRenderer: CampaignDetailsLink, flex: 2},
            {field: "created_on_ts", headerName: "Created On", valueFormatter: timestampFormatter, minWidth: 180,},
            {
                field: "campaign_type",
                headerName: "Campaign Type",
                valueFormatter: ({ value }) =>
                    value === null || value === undefined || value === "" ? "---" : value,
            },
            {field: "user_email",headerName: "User Email", cellRenderer: UserDetailsLink},
            {field: "status", headerName: "Campaign Status", cellRenderer: StatusBadge},
            {field: "reply_rate", headerName: "Reply Rate"}
        ]                           
    
    const campaignExportColumns: CSVColumn<AdminCampaignExportItem>[] = [
        { header: "Campaign UID", accessor: "uid" },
        { header: "Campaign Name", accessor: "name" },
        { header: "User Email", accessor: "user_email" },
        { header: "Workspace ID", accessor: "workspace_id" },
        { header: "Status", accessor: "status" },
        { header: "Created On", accessor: "created_on" },
        { header: "Sending Domains", accessor: "sending_domains" },
        { header: "Total Contacts", accessor: "contacts_count" },
        { header: "Emails Sent", accessor: "emails_sent" },
        { header: "Leads Contacted", accessor: "leads_contacted" },
        { header: "Replies Received", accessor: "replies_count" },
        { header: "Total Opens", accessor: "total_open_count" },
        { header: "Unique Opens", accessor: "unique_open_count" },
        { header: "Forced Opens", accessor: "forced_open_count" },
        { header: "Sequence Count", accessor: "sequence_count" },
        { header: "Has Plain Text Email", accessor: (row) => (row.has_plain_text_email ? "Yes" : "No") },
        { header: "Open Tracking", accessor: (row) => (row.open_tracking_enabled ? "Enabled" : "Disabled") },
        { header: "Click Tracking", accessor: (row) => (row.click_tracking_enabled ? "Enabled" : "Disabled") },
        { header: "Bounce Blocked", accessor: (row) => (row.is_campaign_bounce_blocked ? "Yes" : "No") },
        { header: "Daily Sending Limit per Domain", accessor: "daily_email_limit" },
        { header: "Archived", accessor: (row) => (row.archived ? "Yes" : "No") },
        { header: "Started Running At", accessor: "started_running_at" },
        { header: "Completed At", accessor: "completed_at" },
        { header: "Cancelled At", accessor: "cancelled_at" },
    ];
    
    const ExportAdminCampaignsCsvButton: React.FC = () => {        

        const handleExportCampaigns = async () => {
            setExportLoading(true);
            try {                
                const campaignsData: AdminCampaignExportItem[] = fetchAllCampaignExportQuery.data?.data?.data || [];

                if (!campaignsData.length) {
                    enqueueSnackbar("No campaign data available to export.", { variant: "error" });
                    setExportLoading(false);
                    return;
                }
                const success = exportToCSV(campaignsData, campaignExportColumns, getExportFilename(user?.email ?? "unknown", "AllCampaigns"));

                if (success) {
                    enqueueSnackbar("All campaigns exported successfully!", { variant: "success" });
                } else {
                    enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
                }
            } catch (error) {
                console.error("Export error:", error);
                enqueueSnackbar("An error occurred while fetching campaign export data.", { variant: "error" });
            } finally {
                setExportLoading(false);
            }
        };

        return (
            <Button
                startIcon={<Downloading />}
                variant="contained"
                color="primary"
                onClick={handleExportCampaigns}
                disabled={exportLoading || fetchAllCampaignExportQuery.isLoading}
                sx={{
                    px: 3,
                    whiteSpace: "nowrap",
                    minWidth: 220,
                }}
            >
                {fetchAllCampaignExportQuery.isLoading
                    ? "Fetching..."
                    : exportLoading
                    ? "Exporting..."
                    : "Export All Campaigns"}
            </Button>
        );
    };

	if (!pageData && adminAllCampaignsQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (adminAllCampaignsQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={adminAllCampaignsQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={2} sx={{ pb: 4, height: "100%", width: "100%", maxWidth: "100vw", overflowX: "hidden" }}>
				<Stack direction={"row"} spacing={2} alignItems={"center"}>					
					<Typography fontWeight={"bold"} variant={"h5"}>All Campaigns</Typography>
				</Stack>
				<Divider/>
				<Box sx={{ height: "100%", width: "100%", minWidth: 0, flexGrow: 1 }}>
                    <PaginatedServerSideDataTable
                        columns={columnDefs}
                        paginationData={pageData}
                        loading={
                            adminAllCampaignsQuery.isLoading ||
                            adminAllCampaignsQuery.isFetching
                        }
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Search campaign ID, name, email or status..."
                        actions={
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <TableTopRow
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                />

                                <ExportAdminCampaignsCsvButton />
                            </Stack>
                        }
                        columnLabels={columnCustomizerLabel}
                    />
				</Box>
			</Stack>
		)
	} else {
		// Ideally it should not reach here.
		return <></>
	}
}